// src/layouts/AdminLayout.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, Outlet, Link, useLocation } from "react-router-dom";

const STORAGE_COLLAPSED = "admin_sidebar_collapsed";

const cn = (...a) => a.filter(Boolean).join(" ");

/* ---------- Tiny icon set (inline SVG, no deps) ---------- */
const Icon = {
  dashboard: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M3 12h8V3H3v9zm10 9h8v-7h-8v7zM3 21h8v-7H3v7zm10-9h8V3h-8v9z" />
    </svg>
  ),
  events: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M8 2v4M16 2v4M3 10h18M5 6h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2z" />
    </svg>
  ),
  orders: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M6 2l3 3h9a2 2 0 012 2v3H4V4a2 2 0 012-2zM4 10l1.5 9A2 2 0 007.5 21h9a2 2 0 002-2L20 10H4z" />
    </svg>
  ),
  banners: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M3 4h18v8H3zM7 20l5-4 5 4" />
    </svg>
  ),
  coupon: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M3 7h18v4a2 2 0 100 4v4H3v-4a2 2 0 100-4V7z" />
      <path d="M8 7v10M16 7v10" />
    </svg>
  ),
  review: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.25l-7.19-.62L12 2 9.19 8.63 2 9.25l5.46 4.72L5.82 21z" />
    </svg>
  ),
  payment: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <path d="M2 10h20" />
    </svg>
  ),
  whatsapp: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M12 2a10 10 0 00-8.9 14.6L2 22l5.6-1.5A10 10 0 1012 2z" />
      <path d="M8.5 9.5c.2-.9 1-.9 1.3-.9.3 0 .7 0 1 .6.3.6 1 2.4 1.1 2.6.1.2.1.4 0 .6-.1.2-.5.7-.7.9-.2.2-.4.3-.1.7.3.4 1.2 1.9 2.6 3.1 1.8 1.6 3.3 2.1 3.8 2.3.5.2.8.2 1.1-.1.3-.3 1.3-1.5 1.6-2 .3-.5.6-.4 1-.2.4.2 2.5 1.2 2.9 1.3.4.2.7.3.8.5.1.2.1 1.1-.3 2.1-.4 1-1.8 1.7-2.5 1.8-.7.1-1.5.1-2.3-.1-.8-.2-1.9-.6-3.3-1.4-2.5-1.4-4.1-3.6-4.2-3.8-.1-.2-.9-1.2-.9-2.3 0-1.1.6-1.6.8-1.8z" />
    </svg>
  ),
  search: (
    <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="11" cy="11" r="8" />
      <path d="M21 21l-3.5-3.5" />
    </svg>
  ),
  menu: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 6h18M3 12h18M3 18h18" />
    </svg>
  ),
  close: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M6 18L18 6M6 6l12 12" />
    </svg>
  ),
  chevronLeft: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  chevronRight: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  user: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M12 12a5 5 0 100-10 5 5 0 000 10zM6 22a6 6 0 0112 0" />
    </svg>
  ),
  cashBooking: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="3" y="6" width="18" height="12" rx="2" />
      <path d="M11 10h3.2M11 11.7h3.2" strokeLinecap="round" />
      <path d="M11 10c2 0 2 3 0 3M11 9.2v5.6" strokeLinecap="round" />
    </svg>
  ),
  userList: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8">
      <circle cx="8" cy="8" r="3" />
      <circle cx="17" cy="9" r="3" />
      <path d="M4 20c0-2.5 2-4.5 4.5-4.5S13 17.5 13 20" />
      <path d="M14 20c0-2.5 2-4.5 4.5-4.5S23 17.5 23 20" />
    </svg>
  ),
  allBookings: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M4 6h16a2 2 0 012 2v2a2 2 0 010 4v2a2 2 0 01-2 2H4a2 2 0 01-2-2v-2a2 2 0 010-4V8a2 2 0 012-2z" />
      <path strokeDasharray="2 2" d="M12 6v12" />
      <path d="M7 9h3M7 12h3M7 15h3" strokeLinecap="round" />
      <path d="M15 13l2 2 3-3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
};

const NAV_SECTIONS = [
  {
    title: "Overview",
    items: [{ to: "/admin", label: "Dashboard", icon: Icon.dashboard, end: true }],
  },
  {
    title: "Manage",
    items: [
      { to: "/admin/users", label: "Users List", icon: Icon.userList },
      { to: "/admin/events", label: "Events", icon: Icon.events },
      { to: "/admin/orders", label: "Orders", icon: Icon.orders },
      { to: "/admin/banners", label: "Banners", icon: Icon.banners },
      { to: "/admin/coupon", label: "Discount Coupon", icon: Icon.coupon },
      { to: "/admin/review", label: "Review List", icon: Icon.review },
      { to: "/admin/payment", label: "Payment", icon: Icon.payment },
      { to: "/admin/booking-notifications", label: "Booking notifications", icon: Icon.whatsapp },
      { to: "/admin/payment-reconciliation", label: "Payment reconciliation", icon: Icon.payment },
      { to: "/admin/payment-errors", label: "Payment errors", icon: Icon.review },
      { to: "/admin/cash-booking", label: "Cash Booking", icon: Icon.cashBooking },
      { to: "/admin/allBookingList", label: "All Booking List", icon: Icon.allBookings },
    ],
  },
];

function NavItem({ to, children, icon, collapsed, end, onNavigate }) {
  const label = typeof children === "string" ? children : "";
  return (
    <NavLink
      to={to}
      end={end}
      title={collapsed ? label : undefined}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          "group relative flex items-center rounded-xl text-sm font-semibold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-amber-300",
          collapsed ? "min-h-[44px] justify-center px-2 py-2.5" : "min-h-[44px] gap-3 px-3 py-2.5 sm:min-h-0",
          isActive
            ? "bg-gradient-to-r from-amber-100 to-orange-50 text-slate-900 shadow-sm ring-1 ring-amber-200/80 dark:from-amber-600/90 dark:to-amber-700/80 dark:text-white"
            : "text-slate-600 hover:bg-amber-50/80 dark:text-gray-300 dark:hover:bg-gray-800/60"
        )
      }
      aria-label={label || undefined}
    >
      {({ isActive }) => (
        <>
          <span className="text-amber-600 dark:text-amber-300">{icon}</span>
          {!collapsed && <span className="min-w-0 flex-1 truncate">{children}</span>}
          {!collapsed && (
            <span
              className={cn("h-2 w-2 shrink-0 rounded-full", isActive ? "bg-amber-500" : "bg-transparent")}
              aria-hidden
            />
          )}
          {collapsed && isActive && (
            <span
              className="absolute right-0 top-1/2 h-6 w-1 -translate-y-1/2 rounded-l-full bg-amber-500"
              aria-hidden
            />
          )}
          {collapsed && label && (
            <span className="pointer-events-none absolute left-full z-[60] ml-2 hidden whitespace-nowrap rounded-lg bg-slate-900 px-2.5 py-1.5 text-xs font-medium text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100 lg:group-hover:block">
              {label}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

function Breadcrumbs() {
  const { pathname } = useLocation();
  const parts = pathname.split("/").filter(Boolean);
  const crumbs = useMemo(
    () =>
      parts.map((p, i) => ({
        name: p.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
        to: "/" + parts.slice(0, i + 1).join("/"),
      })),
    [parts]
  );
  if (crumbs.length <= 1) return null;

  return (
    <nav className="text-xs text-slate-500 dark:text-slate-400 md:text-sm" aria-label="Breadcrumb">
      <ol className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <li>
          <Link to="/admin" className="hover:text-amber-600">
            Admin
          </Link>
        </li>
        {crumbs.slice(1).map((c, idx) => (
          <li key={c.to} className="flex items-center gap-2">
            <span className="opacity-40">/</span>
            {idx === crumbs.length - 2 ? (
              <span className="font-medium text-slate-700 dark:text-slate-200">{c.name}</span>
            ) : (
              <Link to={c.to} className="hover:text-amber-600">
                {c.name}
              </Link>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

function initialsFromStoredUser(u) {
  if (!u) return "A";
  const name = String(u.name || "").trim();
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return parts[0].slice(0, 2).toUpperCase();
  }
  const digits = String(u.phone || u.mobile || "").replace(/\D/g, "");
  if (digits.length >= 2) return digits.slice(-2);
  const em = String(u.email || "");
  if (em.length >= 2) return em.slice(0, 2).toUpperCase();
  return "A";
}

function TopBar({ mobileOpen, onToggleMobile, collapsed, onToggleCollapse, headerRef, searchOpen, onToggleSearch }) {
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("bb_user") || "null");
    } catch {
      return null;
    }
  }, []);
  const displayName = user?.name || user?.phone || user?.email || "Administrator";
  const initials = initialsFromStoredUser(user);
  const roleLabel =
    String(user?.role || "").toLowerCase() === "admin" ? "Administrator" : String(user?.role || "Staff");

  return (
    <header
      ref={headerRef}
      className="sticky top-0 z-[100] shrink-0 border-b border-amber-100/60 bg-white/95 backdrop-blur-md dark:border-gray-800 dark:bg-gray-900/95"
    >
      <div
        className="h-[2px] bg-gradient-to-r from-amber-400 via-yellow-400 to-orange-400"
        style={{ backgroundSize: "200% 200%", animation: "goldShift 7s ease infinite" }}
      />
      <style>{`
        @keyframes goldShift {
          0% { background-position: 0% 50% }
          50% { background-position: 100% 50% }
          100% { background-position: 0% 50% }
        }
        @media (prefers-reduced-motion: reduce) {
          [style*="goldShift"] { animation: none !important; }
        }
      `}</style>

      <div className="flex min-w-0 items-center gap-2 px-3 py-2 sm:gap-2.5 sm:px-4">
        <button
          type="button"
          onClick={onToggleMobile}
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 hover:bg-amber-50 focus:outline-none focus:ring-2 focus:ring-amber-300 dark:border-gray-700 dark:hover:bg-gray-800 lg:hidden"
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
          aria-controls="admin-sidebar"
        >
          {mobileOpen ? Icon.close : Icon.menu}
        </button>

        <button
          type="button"
          onClick={onToggleCollapse}
          className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 hover:bg-amber-50 focus:outline-none focus:ring-2 focus:ring-amber-300 dark:border-gray-700 dark:hover:bg-gray-800 lg:inline-flex"
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-expanded={!collapsed}
        >
          {collapsed ? Icon.chevronRight : Icon.chevronLeft}
        </button>

        <Link to="/admin" className="flex min-w-0 flex-1 items-center gap-2 lg:flex-none">
          <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-amber-400 text-sm font-black text-slate-900 shadow-sm">
            ✦
          </div>
          <div className="min-w-0 truncate text-sm font-extrabold tracking-tight sm:text-base">
            <span className="text-slate-900 dark:text-white">Admin</span>
            <span className="text-amber-600">Panel</span>
          </div>
        </Link>

        <button
          type="button"
          onClick={onToggleSearch}
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 hover:bg-amber-50 lg:hidden"
          aria-label={searchOpen ? "Hide search" : "Show search"}
          aria-expanded={searchOpen}
        >
          {Icon.search}
        </button>

        <form className="ml-auto hidden min-w-0 max-w-xs flex-1 lg:block lg:max-w-sm" role="search">
          <div className="relative">
            <input
              placeholder="Search dashboards, events…"
              className="w-full min-w-0 rounded-xl border border-slate-200 bg-white py-2.5 pl-9 pr-3 text-sm focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-200/80 dark:border-gray-700 dark:bg-gray-950 dark:text-slate-100"
            />
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">{Icon.search}</span>
          </div>
        </form>

        <Link
          to="/profile"
          className="inline-flex min-w-0 shrink-0 items-center rounded-full border border-slate-200 p-1 hover:bg-amber-50 focus:outline-none focus:ring-2 focus:ring-amber-300 dark:border-gray-700 dark:hover:bg-gray-800 sm:gap-2 sm:px-2 sm:py-1"
        >
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-gradient-to-tr from-amber-400 to-amber-600 text-[11px] font-bold text-white">
            {initials}
          </span>
          <span className="hidden max-w-[100px] flex-col leading-tight md:flex">
            <span className="truncate text-sm font-medium text-slate-700 dark:text-slate-200">{displayName}</span>
            <span className="truncate text-[10px] capitalize text-slate-500">{roleLabel}</span>
          </span>
        </Link>
      </div>

      {searchOpen && (
        <div className="border-t border-amber-100/60 px-3 pb-3 lg:hidden dark:border-gray-800">
          <form role="search" className="pt-2">
            <div className="relative">
              <input
                placeholder="Search admin…"
                className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-9 pr-3 text-base focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-200/80 dark:border-gray-700 dark:bg-gray-950"
              />
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">{Icon.search}</span>
            </div>
          </form>
        </div>
      )}
    </header>
  );
}

function Sidebar({ collapsed, mobileOpen, onCloseMobile, onNavigate, topOffset }) {
  const showLabels = !collapsed || mobileOpen;
  const offsetStyle = { "--admin-top": `${topOffset}px` };

  return (
    <>
      <div
        role="presentation"
        onClick={onCloseMobile}
        className={cn(
          "fixed inset-x-0 bottom-0 z-[90] bg-black/55 transition-opacity duration-300 lg:hidden",
          "top-[var(--admin-top)]",
          mobileOpen ? "opacity-100" : "pointer-events-none opacity-0"
        )}
        style={offsetStyle}
        aria-hidden={!mobileOpen}
      />

      <aside
        id="admin-sidebar"
        style={offsetStyle}
        className={cn(
          "flex flex-col border-r border-amber-100/70 bg-gradient-to-b from-white to-amber-50/40 transition-[width,transform] duration-300 ease-in-out dark:border-gray-800 dark:from-gray-900 dark:to-gray-950",
          "fixed left-0 z-[95] h-[calc(100dvh-var(--admin-top))] w-[min(300px,92vw)] top-[var(--admin-top)] lg:relative lg:top-auto lg:z-auto lg:h-full lg:w-72 lg:translate-x-0",
          mobileOpen ? "translate-x-0 shadow-2xl" : "-translate-x-full lg:translate-x-0 lg:shadow-none",
          collapsed && "lg:w-[4.75rem]"
        )}
        aria-label="Admin navigation"
      >
        {/* Mobile drawer header */}
        <div className="flex items-center justify-between border-b border-amber-100/80 px-4 py-3 dark:border-gray-800 lg:hidden">
          <span className="text-sm font-bold text-slate-900 dark:text-white">Menu</span>
          <button
            type="button"
            onClick={onCloseMobile}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border hover:bg-amber-50 dark:hover:bg-gray-800"
            aria-label="Close menu"
          >
            {Icon.close}
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden overscroll-contain p-3 pb-[max(1rem,env(safe-area-inset-bottom))] sm:p-4">
          {NAV_SECTIONS.map((section) => (
            <div key={section.title} className={cn(section.title !== "Overview" && "mt-4 sm:mt-5")}>
              {showLabels ? (
                <div className="px-2 text-[11px] font-semibold uppercase tracking-wider text-amber-700/80 dark:text-amber-300/80">
                  {section.title}
                </div>
              ) : (
                <div className="mx-auto my-2 h-px w-8 bg-amber-200/80 dark:bg-gray-700" aria-hidden />
              )}
              <div className={cn("space-y-0.5 sm:space-y-1", showLabels ? "mt-2" : "mt-1")}>
                {section.items.map((item) => (
                  <NavItem
                    key={item.to}
                    to={item.to}
                    icon={item.icon}
                    end={item.end}
                    collapsed={!showLabels}
                    onNavigate={onNavigate}
                  >
                    {item.label}
                  </NavItem>
                ))}
              </div>
            </div>
          ))}

          {showLabels && (
            <div className="mt-6 rounded-xl border border-amber-100/70 bg-gradient-to-br from-amber-50 to-yellow-50 p-4 dark:border-amber-800/40 dark:from-amber-900/20">
              <div className="text-xs font-semibold text-amber-800 dark:text-amber-200">Tip</div>
              <p className="mt-1 text-xs text-amber-900/80 dark:text-amber-200/80">
                Tap the menu icon on mobile. On desktop, use the arrow to collapse the sidebar.
              </p>
              <Link
                to="/"
                onClick={onNavigate}
                className="mt-3 inline-flex w-full items-center justify-center rounded-lg bg-amber-500 px-3 py-2 text-xs font-semibold text-white hover:bg-amber-600"
              >
                Go to Website
              </Link>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}

export default function AdminLayout() {
  const location = useLocation();
  const headerRef = useRef(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(56);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_COLLAPSED) === "true";
    } catch {
      return false;
    }
  });

  const toggleCollapse = () => setCollapsed((v) => !v);

  useEffect(() => {
    const el = headerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const measure = () => setHeaderHeight(el.getBoundingClientRect().height);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [searchOpen, mobileOpen]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_COLLAPSED, collapsed ? "true" : "false");
    } catch {
      /* ignore */
    }
  }, [collapsed]);

  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 1024) setMobileOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const prev = document.body.style.overflow;
    const lock = mobileOpen && typeof window !== "undefined" && window.matchMedia("(max-width: 1023px)").matches;
    document.body.style.overflow = lock ? "hidden" : prev || "";
    return () => {
      document.body.style.overflow = prev || "";
    };
  }, [mobileOpen]);

  useEffect(() => {
    document.documentElement.classList.add("admin-route");
    return () => document.documentElement.classList.remove("admin-route");
  }, []);

  const closeMobile = () => setMobileOpen(false);

  return (
    <div className="admin-shell flex h-[100dvh] w-full min-w-0 flex-col overflow-hidden bg-gradient-to-br from-slate-100 via-white to-amber-50/50 font-sans text-slate-900 dark:from-gray-950 dark:via-gray-950 dark:to-gray-900 dark:text-slate-100">
      <a
        href="#admin-main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-2 focus:top-2 focus:z-[999] focus:rounded-md focus:bg-amber-500 focus:px-3 focus:py-2 focus:text-white"
      >
        Skip to content
      </a>

      <TopBar
        headerRef={headerRef}
        mobileOpen={mobileOpen}
        onToggleMobile={() => setMobileOpen((v) => !v)}
        collapsed={collapsed}
        onToggleCollapse={toggleCollapse}
        searchOpen={searchOpen}
        onToggleSearch={() => setSearchOpen((v) => !v)}
      />

      <div className="flex min-h-0 min-w-0 flex-1 overflow-hidden">
        <Sidebar
          collapsed={collapsed}
          mobileOpen={mobileOpen}
          onCloseMobile={closeMobile}
          onNavigate={closeMobile}
          topOffset={headerHeight}
        />

        <main
          id="admin-main"
          className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-y-contain"
        >
          <div className="mx-auto min-w-0 w-full max-w-full px-2 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-2 sm:px-4 sm:pt-4 md:px-6 md:pt-6">
            <div className="mb-2 sm:mb-3">
              <Breadcrumbs />
            </div>

            <div className="admin-content-card rounded-xl border border-slate-200/80 bg-white/95 p-2.5 shadow-sm ring-1 ring-amber-100/30 dark:border-gray-800 dark:bg-gray-900/90 dark:ring-gray-800 sm:rounded-2xl sm:p-4 sm:shadow-md md:p-6">
              <Outlet />
            </div>

            <p className="py-3 text-center text-[11px] text-slate-500 dark:text-slate-400 sm:py-4 sm:text-xs">
              © {new Date().getFullYear()} ShowTimeBro admin
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}
